import { ItemsForTotalType } from 'config/types/catalog.type';
import CartUtils from 'config/utils/cart.util';
import { store } from '../../../store';
import { CartItemAction, Item } from '../../../store/models/cart.model';
import { Product, ProductVariant } from '../../../store/models/catalog.model';
import { Search } from 'store/models/search.model';
import { ProductImage as ProductImageType } from 'store/models/catalog.model';

const ITEMS_IN_SLEKELETON = 8;
const ITEMS_SKELETON_IN_CAROUSEL = 4;
const DEFAULT_PRODUCT_IMAGE = 'https://gw.alipayobjects.com/zos/rmsportal/JiqGstEfoWAOHiTxclqi.png';
const MAX_VISIBLE_TAGS = 3;

export enum FiltersNameKey {
  PRICE = 'PRICE',
  BRANDS = 'BRANDS',
}

export enum SizeCardControlTypes {
  DEFAULT = 'DEFAULT',
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
}

export enum AppTagColor {
  PROCESSING = 'processing',
  SUCCESS = 'success',
  ERROR = 'error',
  WARNING = 'warning',
  MAGENTA = 'magenta',
  RED = 'red',
  VOLCANO = 'volcano',
  ORANGE = 'orange',
  GOLD = 'gold',
  LIME = 'lime',
  GREEEN = 'green',
  CYAN = 'cyan',
  BLUE = 'blue',
  GEEKBLUE = 'geekblue',
  PURPLE = 'purple',
}

export enum CardControlScope {
  PLP = 'PLP',
  PDP = 'PDP',
  MINI_CART = 'MINI_CART',
}

const addToCart = (product: Pick<Product, '_id' | 'name' | 'slug'>, selectedVariant: ProductVariant): void => {
  CartUtils.addToCart(product, selectedVariant);
};

const increaseOrDecreaseItemInCart = (product: { _id: string }, action: CartItemAction, selectedVariant: ProductVariant): void => {
  CartUtils.increaseOrDecreaseItemInCart(product, action, selectedVariant);
};

const removeToCart = (product: { _id: string }, selectedVariant: ProductVariant): void => {
  CartUtils.removeToCart(product, selectedVariant);
};

const checkProductInCart = (product: { _id: string }, selectedVariant: ProductVariant): boolean => {
  const { items = [] } = store.getState().cart;
  return items.some((item: Item) => CartUtils.isSameCartItem(item, product._id, selectedVariant));
};

const findCartItem = (product: { _id: string }, selectedVariant: ProductVariant, items?: Item[]): Item | undefined => {
  const cartItems = items ?? store.getState().cart.items ?? [];
  return cartItems.find((item) => CartUtils.isSameCartItem(item, product._id, selectedVariant));
};

// TODO: Este metodo hay que corregirlo
const checkProductStock = (product: Product): boolean => {
  const { variant = [] } = product;
  const productHasStock = variant.filter((v) => Number(v.stock) > 0);
  return productHasStock.length > 0;
};

const getLowerValueVariant = (product: Product): ProductVariant => {
  const { variant = [] } = product;
  const eligible = checkProductStock(product) ? variant.filter((v) => Number(v.stock) > 0) : variant;
  return eligible.reduce((lowest, current) => (Number(current.price) < Number(lowest.price) ? current : lowest), eligible[0] ?? {});
};

const getSelectedVariantForProduct = (product: Product, items?: Item[]): ProductVariant => {
  const cartItems = items ?? store.getState().cart.items ?? [];
  const inCartVariant = (product.variant ?? []).find((variant) =>
    cartItems.some((item) => CartUtils.isSameCartItem(item, product._id, variant)),
  );
  return inCartVariant ?? getLowerValueVariant(product);
};

const checkProductInFavorites = (product: { _id: string }, favorites: string[]): boolean => Boolean(favorites.includes(`${product._id}`));

const urlStringPriceToArrayNumberPrice = (stringPrice: string): number[] => stringPrice.split('-').map((item: string) => Number(item));

const isFullPriceRange = (selectedPriceRange: number[], priceRange: number[]): boolean =>
  selectedPriceRange.length === 2 &&
  priceRange.length === 2 &&
  selectedPriceRange[0] === priceRange[0] &&
  selectedPriceRange[1] === priceRange[1];

const getProductReputation = (votes: number, totalVotes: number): number => {
  if (!votes) return 0;
  return Math.min(Math.max(totalVotes / votes, 1), 5);
};

const getItemsToInitPurchasePayload = (items: Item[]): ItemsForTotalType[] =>
  items.map<ItemsForTotalType>(({ product, variant, quantity }) => ({
    product: product._id ?? '',
    sku: variant.sku ?? '',
    quantity: quantity ?? 0,
  }));

const productVariantHasStock = (variant: ProductVariant): boolean => {
  return Number(variant.stock) > 0;
};

const getSearchFootPrint = (search: Search): string =>
  JSON.stringify({
    term: search.searchTerm,
    limit: search.limit,
    page: search.page,
    orderBy: search.orderBy,
    price: search.price,
    brands: search.filteredBrands,
  });

export const getMainProductImage = (images?: ProductImageType[]): ProductImageType | undefined => {
  if (!images || images.length === 0) return undefined;
  return images.find((i: ProductImageType) => i.isMain);
};

export const getOptionalProductImages = (images?: ProductImageType[]): ProductImageType[] => {
  if (!images) return [];
  return images.filter((i: ProductImageType) => !i.isMain);
};

export const getVisibleProductTags = (tags?: string[], limit?: number): string[] => {
  const visible = (tags ?? []).map((tag) => tag?.trim()).filter((tag): tag is string => Boolean(tag));
  return typeof limit === 'number' ? visible.slice(0, limit) : visible;
};

const CatalogUtils = {
  ITEMS_IN_SLEKELETON,
  ITEMS_SKELETON_IN_CAROUSEL,
  DEFAULT_PRODUCT_IMAGE,
  MAX_VISIBLE_TAGS,
  addToCart,
  increaseOrDecreaseItemInCart,
  removeToCart,
  checkProductInCart,
  findCartItem,
  getSelectedVariantForProduct,
  checkProductInFavorites,
  urlStringPriceToArrayNumberPrice,
  isFullPriceRange,
  getLowerValueVariant,
  checkProductStock,
  getProductReputation,
  getItemsToInitPurchasePayload,
  productVariantHasStock,
  getSearchFootPrint,
  getMainProductImage,
  getOptionalProductImages,
  getVisibleProductTags,
};

export default CatalogUtils;
